import { Box, Flex, IconButton } from "@radix-ui/themes";
import { Pencil, X } from "lucide-react";
import { DashboardWidget, ListData } from "@/types/breeze/Dashboard";
import { ListWidget } from "./widgets/ListWidget";
import { ChartWidget } from "./widgets/ChartWidget";
import { QuickActionWidget } from "./widgets/QuickActionWidget";
import { MarkdownWidget } from "./widgets/MarkdownWidget";
import { MediaWidget } from "./widgets/MediaWidget";
import { GettingStartedWidget } from "./widgets/GettingStartedWidget";
import { MaterialisedViewWidget } from "./widgets/MaterialisedViewWidget";
import { MaterialisedViewData } from "@/types/breeze/MaterialisedView";

interface Props {
  widget: DashboardWidget;
  data?: unknown;
  loading?: boolean;
  editing?: boolean;
  onRemove?: (widgetId: number) => void;
  onEdit?: (widget: DashboardWidget) => void;
}

export function WidgetRenderer({ widget, data, loading, editing, onRemove, onEdit }: Props) {
  return (
    <Box
      style={{
        height: "100%",
        backgroundColor: "var(--color-panel-solid)",
        border: "1px solid var(--gray-5)",
        borderRadius: "var(--radius-3)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {editing && (
        <Flex
          gap="1"
          style={{ position: "absolute", top: "8px", right: "8px", zIndex: 2 }}
        >
          {onEdit && (
            <IconButton
              size="1"
              variant="soft"
              onClick={() => onEdit(widget)}
              aria-label="Edit widget"
            >
              <Pencil size={14} />
            </IconButton>
          )}
          {onRemove && (
            <IconButton
              size="1"
              variant="soft"
              color="red"
              onClick={() => onRemove(widget.id)}
              aria-label="Remove widget"
            >
              <X size={14} />
            </IconButton>
          )}
        </Flex>
      )}
      {renderByType(widget, data, loading)}
    </Box>
  );
}

function renderByType(widget: DashboardWidget, data: unknown, loading?: boolean) {
  switch (widget.type) {
    case "list":
      return <ListWidget widget={widget} data={data as ListData | undefined} loading={loading} />;
    case "chart":
      return <ChartWidget widget={widget} data={data as Parameters<typeof ChartWidget>[0]["data"]} loading={loading} />;
    case "materialised_view":
      return <MaterialisedViewWidget widget={widget} data={data as MaterialisedViewData | undefined} loading={loading} />;
    case "quick_action":
      return <QuickActionWidget widget={widget} />;
    case "markdown":
      return <MarkdownWidget widget={widget} />;
    case "media":
      return <MediaWidget widget={widget} />;
    case "getting_started":
      return <GettingStartedWidget widget={widget} />;
    default:
      return null;
  }
}
